/* eslint-disable no-empty */
/**
 * CallRecordingsPage.jsx  ·  v3.21-1634 · Block 3
 *
 * Internal-only Phone System → Call Recordings.  Lists recorded calls
 * newest-first with client/phone/rep/direction/duration/disposition/
 * recording-status columns, filters (search, direction, disposition,
 * date range, rep), and an inline audio player that streams via a
 * short-lived signed Hub URL (no Twilio credentials or raw media URL
 * ever reach the browser).
 *
 * External partners are denied at the API layer — this page renders
 * an internal-only note if a non-internal role happens to land here.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { playbackErrorText } from '../components/VoicemailAudioPlayer';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  PhoneCall, PhoneIncoming, PhoneOutgoing, Play, Pause, Search,
  Clock, ShieldAlert, User as UserIcon,
} from 'lucide-react';

const _fmtSec = (s) => {
  if (!s && s !== 0) return '—';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};
const _fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const RecordingPlayer = ({ row, onEnded }) => {
  const [mediaUrl, setMediaUrl] = useState(null);
  const [loading, setLoading]   = useState(false);
  const [playing, setPlaying]   = useState(false);
  const [pos, setPos]           = useState(0);
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `/telephony/recordings/${row.id}/media-token`,
        { intent: 'play' }
      );
      // Absolute URL under the api base — attach the same auth header
      // by fetching via api client and creating an object URL.
      // v3.21-1836 · media_url already starts with /api; strip it so axios' /api baseURL isn't doubled.
      const audioRes = await api.get(data.media_url.replace(/^\/api/, ''), { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(audioRes.data);
      setMediaUrl(blobUrl);
      setTimeout(() => {
        try { audioRef.current?.play(); setPlaying(true); } catch (_) {}
      }, 100);
    } catch (e) {
      toast.error(await playbackErrorText(e));
    } finally { setLoading(false); }
  }, [row.id]);

  useEffect(() => () => { if (mediaUrl) URL.revokeObjectURL(mediaUrl); }, [mediaUrl]);

  if (row.recording_status === 'in-progress') {
    return <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">Processing Recording</Badge>;
  }
  if (!row.has_media) {
    return <span className="text-xs text-slate-500">No Recording Available</span>;
  }
  if (!mediaUrl) {
    return (
      <Button size="sm" variant="outline" disabled={loading}
              onClick={load}
              data-testid={`play-recording-${row.id}`}>
        <Play className="w-3.5 h-3.5 mr-1" />
        {loading ? 'Loading…' : 'Play Recording'}
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2 min-w-[280px]">
      <audio
        ref={audioRef}
        src={mediaUrl}
        controls
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); onEnded?.(); }}
        onTimeUpdate={(e) => setPos(e.currentTarget.currentTime || 0)}
        className="h-8"
        data-testid={`audio-${row.id}`}
      />
      <span className="text-[11px] text-slate-500">
        <Clock className="w-3 h-3 inline mr-0.5" />
        {_fmtSec(pos)} / {_fmtSec(row.duration)}
      </span>
    </div>
  );
};

const CallRecordingsPage = () => {
  const { user, isInternal } = useAuth();
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [q, setQ]               = useState('');
  const [direction, setDirection] = useState('all');
  const [disposition, setDisposition] = useState('all');
  const [onlyMedia, setOnlyMedia] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]     = useState('');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, only_with_media: onlyMedia };
      if (q) params.q = q;
      if (direction !== 'all')   params.direction = direction;
      if (disposition !== 'all') params.disposition = disposition;
      if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
      if (dateTo)   params.date_to   = new Date(dateTo).toISOString();
      const { data } = await api.get('/telephony/recordings', { params });
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 403) {
        toast.error('Call recordings are internal-only.');
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load recordings');
      }
      setRows([]);
    } finally { setLoading(false); }
  }, [q, direction, disposition, onlyMedia, dateFrom, dateTo]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const dispositions = useMemo(() => {
    const seen = new Set();
    for (const r of rows) if (r.disposition) seen.add(r.disposition);
    return Array.from(seen).sort();
  }, [rows]);

  if (!isInternal()) {
    return (
      <Card data-testid="call-recordings-denied">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-red-500" /> Internal Only
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600">
            Call recordings are internal to Prestigious Companies staff and
            are not available to external partner accounts.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="call-recordings-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-primary">Call Recordings</h1>
        <p className="text-slate-500 mt-1 text-sm">
          Internal SSOT for outbound + inbound call recordings.  Newest first.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6 pb-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-2 top-3 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Search client, phone, rep, disposition…"
                     className="pl-8" data-testid="recordings-search" />
            </div>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="recordings-direction"><SelectValue placeholder="Direction" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All directions</SelectItem>
                <SelectItem value="inbound">Inbound</SelectItem>
                <SelectItem value="outbound">Outbound</SelectItem>
              </SelectContent>
            </Select>
            <Select value={disposition} onValueChange={setDisposition}>
              <SelectTrigger data-testid="recordings-disposition"><SelectValue placeholder="Disposition" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All dispositions</SelectItem>
                {dispositions.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   placeholder="From" data-testid="recordings-from" />
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   placeholder="To" data-testid="recordings-to" />
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <label className="flex items-center gap-1 cursor-pointer">
              <input type="checkbox" checked={onlyMedia}
                     onChange={(e) => setOnlyMedia(e.target.checked)}
                     data-testid="recordings-only-media" />
              Only calls with recorded media
            </label>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{loading ? 'Loading…' : `${rows.length} of ${total} recordings`}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3">Date / Time</th>
                  <th className="text-left p-3">Direction</th>
                  <th className="text-left p-3">Client</th>
                  <th className="text-left p-3">Phone</th>
                  <th className="text-left p-3">Rep</th>
                  <th className="text-left p-3">Duration</th>
                  <th className="text-left p-3">Disposition</th>
                  <th className="text-left p-3">Recording</th>
                </tr>
              </thead>
              <tbody data-testid="recordings-tbody">
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="p-3 text-xs">{_fmtDate(r.created_at)}</td>
                    <td className="p-3">
                      {r.direction === 'inbound' ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200"><PhoneIncoming className="w-3 h-3 mr-1" /> Inbound</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200"><PhoneOutgoing className="w-3 h-3 mr-1" /> Outbound</Badge>
                      )}
                    </td>
                    <td className="p-3">
                      {r.client_id ? (
                        <a href={`/clients/${r.client_id}`} className="text-primary underline underline-offset-2">
                          {r.client_name || '—'}
                        </a>
                      ) : (
                        <span className="text-slate-500 italic">
                          <UserIcon className="w-3 h-3 inline mr-0.5" />
                          Unknown caller
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-xs">{r.phone || '—'}</td>
                    <td className="p-3 text-xs">{r.rep_name || '—'}</td>
                    <td className="p-3 text-xs">{_fmtSec(r.duration)}</td>
                    <td className="p-3 text-xs">{r.disposition || '—'}</td>
                    <td className="p-3"><RecordingPlayer row={r} /></td>
                  </tr>
                ))}
                {(!loading && rows.length === 0) && (
                  <tr><td colSpan={8} className="text-center text-slate-500 py-10">No recordings match the current filters</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CallRecordingsPage;
